import * as angular from "angular";
import HttpServiceName, { HttpService } from "../http/http.service";
import * as Highcharts from 'highcharts/highstock';
import HC_more from 'highcharts/highcharts-more';
HC_more(Highcharts);
import solidGauge from 'highcharts/modules/solid-gauge';
solidGauge(Highcharts);

export interface ChartConfigService {
  getDailyPackChartConfig();
  getMonthlyPackChartConfig();
  getYearlyPackChartConfig();
  getDailyFurnaceChartConfig();
  getMonthlyFurnaceChartConfig();
  getYearlyFurnaceChartConfig();
  getDailyLineHoldsChartConfig();
  getHelpText(chartType: string, isShop: boolean);
}

let perecentText = 'Percent';
let aopMonthText = 'AOP Month';
let percentOfAopText = '% of AOP';
let tonsText = 'Tons';
let furnaceUtilizationText = 'Furnace Utilization';
let percentLineHoldsText = '% Line Holds';
let chartHeight = 275;
let red = '#f04124';
let green = '#358753';
let gray = '#A5A2A2';
let black = '#000000';
let white = '#FFFFFF';
let lightBlue = '#BBDEFB';
let darkBlue = '#0303BA';
let legendInstructions = 'click on any item in the legend above to hide it from the chart';

class ChartConfigServiceImpl implements ChartConfigService {
  private httpService: HttpService;

  constructor(httpService: HttpService) {
    this.httpService = httpService;
  }

  getDailyPackChartConfig() {
    return this.dailyPackChartConfig;
  }

  getMonthlyPackChartConfig() {
    return this.monthlyPackChartConfig;
  }

  getYearlyPackChartConfig() {
    return this.ytdPackChartConfig;
  }

  getDailyFurnaceChartConfig() {
    return this.dailyFurnaceChartConfig;
  }

  getMonthlyFurnaceChartConfig() {
    return this.monthlyFurnaceChartConfig;
  }

  getYearlyFurnaceChartConfig() {
    return this.ytdFurnaceChartConfig;
  }

  getDailyLineHoldsChartConfig() {
    return this.dailyLineHoldsChartConfig;
  }

  getHelpText(chartType: string, isShop: boolean) {
    if (chartType === 'daily') {
      if (isShop) {
        return this.dailyPackHelpText;
      }
      return this.dailyPTMHelpText;
    }

    if (chartType === 'monthly') {
      if (isShop) {
        return this.monthlyPackHelpText;
      }
      return this.monthlyPTMHelpText;
    }

    if (chartType === 'yearly') {
      if (isShop) {
        return this.yearlyPackHelpText;
      }
      return this.yearlyPTMHelpText;
    }

    if (chartType === 'packProducts') {
      return this.packProductHelpText;
    }

    if (chartType === 'lineHolds') {
      return this.lineHoldsHelpText;
    }

    if (chartType === 'dailyFurnace') {
      return this.dailyFurnaceHelpText;
    }

    if (chartType === 'monthlyFurnace') {
      return this.monthlyFurnaceHelpText;
    }

    if (chartType === 'yearlyFurnace') {
      return this.ytdFurnaceHelpText;
    }

    return "help text not found";
  }

  packProductHelpText: string = `
  <div class="header">
    Top 5 / Bottom 5 Pack Products
  </div>
  <div class="subheader">
    <span> Data Source: </span>
    <span class="detail">Legacy AS400</span>
  </div>    
  <div class="detail">`
    + `This section displays the top 5 and bottom 5 products for the selected plant, tank, or shop, `
    + `based on <b>Percent Pack</b> for the selected month and/or year.
  </div> 
  <div class="detail">
    Percent Pack is calculated from data in Job Records in the legacy AS400 system.  
    All records where speed > 0 and production hours > 0 are included.<br/>
  </div>
  <div class="detail">
    <span class="formula">% Pack = rounded to tenths(Gross / Shearcuts * 100)</span><br/>
    <span class="footnote">[where shearcuts > 0]</span><br/><br/>
    <span class="formula">Shearcuts = Actual Speed * 60 * Actual Hours / 144</span>
  </div>
  `;

  // Daily Pack Chart Config
  dailyPackChartConfig: any = {
    chart: {
      alignTicks: true,
      zoomType: 'xy',
      height: chartHeight,
      spacingBottom: 5,
      borderWidth: 2,
      borderColor: '#DDDDDD'
    },
    credits: {
      enabled: false
    },
    title: {
      text: '',
      margin: 0,
      useHTML: true
    },
    subtitle: {
      text: legendInstructions,
      align: 'center',
      verticalAlign: 'bottom',
      y: -5,
      style: {
        color: gray
      }
    },
    xAxis: {
      categories: []
    },
    tooltip: {
      valueDecimals: 1,
      valueSuffix: '%'
    },
    yAxis: {
      title: {
        text: perecentText
      },
      max: 125,
      tickInterval: 25,
      plotLines: [{
        color: black,
        dashStyle: 'dash',
        value: 0,
        width: 3,
        label: {
          text: aopMonthText,
          style: {
            color: black
          }
        }
      }]
    },
    plotOptions: {
      column: {
        dataLabels: {
          enabled: true,
          rotation: -90,
          color: white,
          align: 'right',
          format: '{point.y:.1f}', // one decimal
          y: 10, // 10 pixels down from the top
          style: {
            fontSize: '11px',
            fontFamily: 'Verdana, sans-serif'
          }
        }
      }
    },
    series: [{
      type: 'column',
      data: [],
      color: black,
      name: ''
    },
    {
      type: 'column',
      data: [],
      color: darkBlue,
      name: ''
    },
    ],
    responsive: {
      rules: [{
        condition: {
          maxWidth: 400
        },
        chartOptions: {
          plotOptions: {
            column: {
              dataLabels: {
                enabled: false
              }
            }
          }
        }

      }]
    }
  };

  // Daily PTM Help Text
  dailyPTMHelpText: string = `
    <div class="header">
      Month To Date PTM
    </div>
    <div class="subheader">
      <span> Data Source: </span>
      <span class="detail">Legacy AS400</span>
    </div>    
    <div class="detail">The chart displays <b>Current Year (CY) and Previous Year (PY) Pack To Melt</b>
     for each day in the selected month. <br/> <span class="footnote">(PTM is displayed only when a plant 
      or tank is selected on the dashboard; when a shop is selected, PTP is displayed.)</span>
    </div>
    <div class="detail">
      A <span class="green">green trend line</span> is also displayed, showing the <b>Annual Operating Plan 
      (AOP)</b> value for the selected month,
      making it easy to see how each day is measuring up to the planned amount.  
      The AOP values are pulled from yearly spreadsheets maintained at corporate.
    </div>
    <div class="detail">
      <span class="formula">PTM = Packed Tons / Melted Tons * 100</span><br/>
      <span class="footnote">[where Melted Tons > 0]</span>
    </div>
    <div class="detail">
      <b>Melted Tons</b> is taken from the Materials Usage / Commodity File in the legacy AS400 system, 
      summarized by plant, tank, and date.<br/>
      <span class="formula">Melted Tons = SUM(LBS * Yield / 100 / 2000)</span><br/>
      <span class="footnote">[where LBS is # of batches * pounds of material and Yield is % of 
        material consumed from Commodity File]</span>
    </div>
    <div class="detail">
      <b>Packed Tons</b> is taken from Job Records in the legacy AS400 system, summarized by 
      plant, tank, and date.<br/>
      <span class="formula">Packed Tons = SUM(Weight * Gross * .0045)</span><br/>
      <span class="footnote">[where .0045 is 144/32000 to convert oz-gross to tons]</span>
    </div>
    `;

  // Daily Pack Help Text
  dailyPackHelpText: string = `
    <div class="header">
      Month To Date PTP
    </div>
    <div class="subheader">
      <span> Data Source: </span>
      <span class="detail">Legacy AS400</span>
    </div>    
    <div class="detail">
      The chart displays <b>Current Year (CY) and Previous Year (PY) Pack To Pull</b> for 
      each day in the selected month.<br/>
      <span class="footnote">(PTP is displayed only when a shop is selected on the dashboard; 
        when a plant or tank is selected, PTM is displayed.)</span>
    </div>
    <div class="detail">
      A <span class="green">green trend line</span> is also displayed, showing the <b>
      Annual Operating Plan (AOP)</b> value for the selected month, making it easy to see 
      how each day is measuring up to the planned amount.  The AOP values are pulled 
      from yearly spreadsheets maintained at corporate.
    </div>
    <div class="detail">
      Pack to Pull is calculated from data in Job Records in the legacy AS400 system.  
      All records where speed > 0 and production hours > 0 are included.<br/>
    </div>
    <div class="detail">
      <span class="formula">PTP = rounded to tenths(Gross / Shearcuts * 100)</span><br/>
      <span class="footnote">[where shearcuts > 0]</span><br/><br/>
      <span class="formula">Shearcuts = Actual Speed * 60 * Actual Hours / 144</span>
    </div>
    `;

  // Monthly Pack Chart Config
  monthlyPackChartConfig: any = {
    chart: {
      alignTicks: true,
      zoomType: 'xy',
      height: chartHeight,
      spacingBottom: 5,
      borderWidth: 2,
      borderColor: '#DDDDDD'
    },
    credits: {
      enabled: false
    },
    title: {
      text: '',
      margin: 0,
      useHTML: true
    },
    subtitle: {
      text: legendInstructions,
      align: 'center',
      verticalAlign: 'bottom',
      y: -5,
      style: {
        color: gray
      }
    },
    plotOptions: {
      column: {
        dataLabels: {
          enabled: true,
          rotation: -90,
          color: white,
          align: 'right',
          format: '{point.y:.1f}', // one decimal
          y: 10, // 10 pixels down from the top
          style: {
            fontSize: '11px',
            fontFamily: 'Verdana, sans-serif'
          }
        }
      },
      spline: {
        marker: {
          radius: 7
        }
      }
    },
    xAxis: {
      categories: []
    },
    tooltip: {
      valueDecimals: 1,
      valueSuffix: '%'
    },
    yAxis: {
      title: {
        text: perecentText
      },
      max: 125,
      tickInterval: 25
    },
    series: [{
      type: 'column',
      data: [],
      color: black,
      name: ''
    },
    {
      type: 'column',
      data: [],
      name: '',
      color: lightBlue
    },
    {
      type: 'column',
      data: [],
      color: darkBlue,
      name: ''
    },
    {
      type: 'spline',
      name: percentOfAopText,
      data: [],
      color: red,
      zones: [
        {
          value: 100,
          color: red // red for anything below 100
        },
        {
          color: green // green for anything 100% and above
        }]
    }],
    responsive: {
      rules: [{
        condition: {
          maxWidth: 400
        },
        chartOptions: {
          plotOptions: {
            column: {
              dataLabels: {
                enabled: false
              }
            }
          }
        }

      }]
    }
  };

  // Monthly PTM Help Text
  monthlyPTMHelpText: string = `
    <div class="header">
      Year To Date PTM
    </div>
    <div class="subheader">
      <span> Data Source: </span>
      <span class="detail">Legacy AS400</span>
    </div>    
    <div class="detail">
      The chart displays <b>Current Year (CY) and Previous Year (PY) Pack To Melt</b>, as well as 
      <b>Annual Operating Plan (AOP)</b> values, for each month in the current year.  
      The AOP values are pulled from yearly spreadsheets maintained at corporate.<br/>  
      <span class="footnote">(PTM is displayed only when a plant or tank is selected on the dashboard; 
        when a shop is selected, PTP is displayed.)</span>
    </div>
    <div class="detail">
      The <b>% of AOP</b> is also displayed as a line graph.  The line markers will 
      be <span class="red">red</span> for any percent <b>below 100</b>
       and <span class="green">green</span> for anything <b>100% and above</b>.<br/><br/>
      <span class="formula">% of AOP = CY Pack to Melt / AOP * 100</span>
    </div>
    <div class="detail">
      <span class="formula">PTM = Packed Tons / Melted Tons * 100</span><br/>
      <span class="footnote">[where Melted Tons > 0]</span>
    </div>
    <div class="detail">
      <b>Melted Tons</b> is taken from the Materials Usage / Commodity File in the legacy 
      AS400 system, summarized by plant, tank, and date.<br/>
      <span class="formula">Melted Tons = SUM(LBS * Yield / 100 / 2000)</span><br/>
      <span class="footnote">[where LBS is # of batches * pounds of material and 
        Yield is % of material consumed from Commodity File]</span>
    </div>
    <div class="detail">
      <b>Packed Tons</b> is taken from Job Records in the legacy AS400 system, summarized by 
      plant, tank, and date.<br/>
      <span class="formula">Packed Tons = SUM(Weight * Gross * .0045)</span><br/>
      <span class="footnote">[where .0045 is 144/32000 to convert oz-gross to tons]</span>
    </div>
    `;

  // Monthly Pack Help Text
  monthlyPackHelpText: string = `
    <div class="header">
      Year To Date PTP
    </div>
    <div class="subheader">
      <span> Data Source: </span>
      <span class="detail">Legacy AS400</span>
    </div>    
    <div class="detail">
      The chart displays <b>Current Year (CY) and Previous Year (PY) Pack To Pull</b>, as
       well as <b>Annual Operating Plan (AOP)</b> values, for each month in the current year.  
      The AOP values are pulled from yearly spreadsheets maintained at corporate.<br/>  
      <span class="footnote">(PTP is displayed only when a shop is selected on the dashboard;
         when a plant or tank is selected, PTM is displayed.)</span>
    </div>
    <div class="detail">
      The <b>% of AOP</b> is also displayed as a line graph.  The line markers will
       be <span class="red">red</span> for any percent <b>below 100</b> and <span class="green">green</span>
        for anything <b>100% and above</b>.<br/><br/>
      <span class="formula">% of AOP = CY Percent Pack / AOP * 100</span>
    </div>
    <div class="detail">
      PTP is calculated from data in Job Records in the legacy AS400 system.  
      All records where speed > 0 and production hours > 0 are included.<br/>
    </div>
    <div class="detail">
      <span class="formula">PTP = rounded to tenths(Gross / Shearcuts * 100)</span><br/>
      <span class="footnote">[where shearcuts > 0]</span><br/><br/>
      <span class="formula">Shearcuts = Actual Speed * 60 * Actual Hours / 144</span>
    </div>
    `;

  // YTD Pack Chart Config
  ytdPackChartConfig: any = {
    chart: {
      type: 'solidgauge',
      width: 280,
      height: 100
    },
    title: 'null',
    pane: {
      center: ['30%', '85%'],
      size: '170%',
      startAngle: -90,
      endAngle: 90,
      background: {
        backgroundColor:
          Highcharts.defaultOptions.legend.backgroundColor || '#EEE',
        innerRadius: '75%',
        outerRadius: '100%',
        shape: 'arc'
      }
    },
    tooltip: {
      enabled: false
    },
    credits: {
      enabled: false
    },
    yAxis: {
      min: 0,
      max: 100,
      stops: [],
      lineWidth: 0,
      minorTickInterval: null,
      tickAmount: 2,
      title: {
        text: '',
        useHTML: true,
        y: -30,
        x: 50
      },
      labels: {
        y: 16,
      },
      plotLines: [{
        color: green,
        width: 1,
        value: 0,
        zIndex: 10,
        label: {
          text: "",
          x: 40,
          y: -10,
          style: {
            color: black,
            fontSize: "10px"
          }
        }
      }]
    },
    plotOptions: {
      solidgauge: {
        innerRadius: '75%',
        dataLabels: {
          y: 5,
          borderWidth: 0,
          useHTML: true
        }
      }
    },
    series: [{
      data: [],
      dataLabels: {
        format: '',
        x: 0,
        y: -1
      }
    }]
  };

  // YTD PTM Help Text
  yearlyPTMHelpText: string = `
    <div class="header">
      YTD PTM
    </div>
    <div class="subheader">
      <span> Data Source: </span>
      <span class="detail">Legacy AS400</span>
    </div>    
    <div class="detail">
      This gauge chart displays the year-to-date <b>Pack To Melt</b> percentage.        
      <span class="footnote">(PTM is displayed only when a plant or tank is selected
         on the dashboard; when a shop is selected, PTP is displayed.)</span>
    </div>
    <div class="detail">
      A <span class="green">green trend line</span> is also displayed, showing the <b>Annual
       Operating Plan (AOP)</b> value for the current year,
      making it easy to see how the year is measuring up to the planned amount.  The AOP values
       are pulled from yearly spreadsheets maintained at corporate.
    </div>
    <div class="detail">
      The <b>% of AOP</b> determines the color of the gauge chart.  It will show
       as <span class="red">red</span> for any percent of AOP <b>below 100</b> and
        <span class="green">green</span> for anything <b>100% and above</b>.<br/><br/>
      <span class="formula">% of AOP = YTD Pack to Melt / AOP * 100</span>
    </div>
    <div class="detail">
      <span class="formula">PTM = Packed Tons / Melted Tons * 100</span><br/>
      <span class="footnote">[where Melted Tons > 0]</span>
    </div>
    <div class="detail">
      <b>Melted Tons</b> is taken from the Materials Usage / Commodity File in the legacy
       AS400 system, summarized by plant, tank, and date.<br/>
      <span class="formula">Melted Tons = SUM(LBS * Yield / 100 / 2000)</span><br/>
      <span class="footnote">[where LBS is # of batches * pounds of material and Yield is % of
         material consumed from Commodity File]</span>
    </div>
    <div class="detail">
      <b>Packed Tons</b> is taken from Job Records in the legacy AS400 system, summarized by
       plant, tank, and date.<br/>
      <span class="formula">Packed Tons = SUM(Weight * Gross * .0045)</span><br/>
      <span class="footnote">[where .0045 is 144/32000 to convert oz-gross to tons]</span>
    </div>
    `;

  // YTD Pack Help Text
  yearlyPackHelpText: string = `
    <div class="header">
      YTD PTP
    </div>
    <div class="subheader">
      <span> Data Source: </span>
      <span class="detail">Legacy AS400</span>
    </div>    
    <div class="detail">
      This gauge chart displays the year-to-date <b>Percent Pack</b> percentage. 
      <span class="footnote">(PTP is displayed only when a shop is selected on the dashboard;
         when a plant or tank is selected, PTM is displayed.)</span>
    </div>
    <div class="detail">
      A <span class="green">green trend line</span> is also displayed, showing the <b>Annual Operating
       Plan (AOP)</b> value for the current year,
      making it easy to see how the year is measuring up to the planned amount.  The AOP values
       are pulled from 
      yearly spreadsheets maintained at corporate.
    </div>
    <div class="detail">
      The <b>% of AOP</b> determines the color of the gauge chart.  It will show
       as <span class="red">red</span> for any percent of AOP <b>below 100</b>
        and <span class="green">green</span> for anything <b>100% and above</b>.<br/><br/>
      <span class="formula">% of AOP = YTD Percent Pack / AOP * 100</span>
    </div>
    <div class="detail">
      PTP is calculated from data in Job Records in the legacy AS400 system.  
      All records where speed > 0 and production hours > 0 are included.<br/>
    </div>
    <div class="detail">
      <span class="formula">PTP = rounded to tenths(Gross / Shearcuts * 100)</span><br/>
      <span class="footnote">[where shearcuts > 0]</span><br/><br/>
      <span class="formula">Shearcuts = Actual Speed * 60 * Actual Hours / 144</span>
    </div>
    `;

  // Daily Furnace Chart Config
  dailyFurnaceChartConfig: any = {
    chart: {
      alignTicks: true,
      zoomType: 'xy',
      height: chartHeight,
      spacingBottom: 5,
      borderWidth: 2,
      borderColor: '#DDDDDD'
    },
    credits: {
      enabled: false
    },
    title: {
      text: '',
      margin: 0,
      useHTML: true
    },
    subtitle: {
      text: legendInstructions,
      align: 'center',
      verticalAlign: 'bottom',
      y: -5,
      style: {
        color: gray
      }
    },
    plotOptions: {
      column: {
        dataLabels: {
          enabled: true,
          rotation: -90,
          color: white,
          align: 'right',
          format: '{point.y:.1f}', // one decimal
          y: 10, // 10 pixels down from the top
          style: {
            fontSize: '11px',
            fontFamily: 'Verdana, sans-serif'
          }
        }
      },
      spline: {
        marker: {
          radius: 7
        }
      }
    },
    xAxis: {
      categories: []
    },
    tooltip: {
      valueDecimals: 1
    },
    yAxis: [{
      title: {
        text: tonsText
      }
    },
    {
      title: {
        text: perecentText
      },
      endOnTick: false,
      startOnTick: false,
      opposite: true,
      tickInterval: 10,
      min: 40,
      max: 100
    }],
    series: [{
      type: 'column',
      data: [],
      color: black,
      name: ''
    },
    {
      type: 'column',
      data: [],
      name: '',
      color: darkBlue
    },
    {
      type: 'spline',
      name: furnaceUtilizationText,
      data: [],
      yAxis: 1,
      color: red,
      tooltip: {
        headerFormat: '<span style="font-size: 10px">{point.key}</span><br/><span '
          + 'style="color:{point.color}">●</span><span>' + furnaceUtilizationText + ':</span>',
        pointFormat: '<b>{point.y}%</b>'
      },
      zones: [
        {
          value: 95,
          color: red // red for anything below 95
        },
        {
          color: green // green for anything 95% and above
        }]
    }],
    responsive: {
      rules: [{
        condition: {
          maxWidth: 400
        },
        chartOptions: {
          plotOptions: {
            column: {
              dataLabels: {
                enabled: false
              }
            }
          }
        }

      }]
    }
  };

  dailyFurnaceHelpText: string = `
    <div class="header">
      Month to Date Furnace Utilization
    </div>
    <div class="subheader">
      <span> Data Source: </span>
      <span class="detail">Legacy AS400</span>
    </div>    
    <div class="detail">
      The chart displays the <b>Tons Melted, Tons Packed, and Furnace Utilization Percentage</b>
       for the selected tank, for each day in the selected month.
    </div>    
    <div class="detail">
      <b>Tons Melted</b> is taken from the Materials Usage / Commodity File in the legacy
       AS400 system, summarized by plant, tank, and date.<br/><br/>
      <span class="formula">Tons Melted = SUM(LBS * Yield / 100 / 2000)</span><br/>
      <span class="footnote">[where LBS is # of batches * pounds of material and Yield is % of material consumed from Commodity File]</span>
    </div>
    <div class="detail">
      <b>Tons Packed</b> is taken from Job Records in the legacy AS400 system, summarized by plant, tank, and date.<br/><br/>
      <span class="formula">Tons Packed = SUM(Weight * Gross * .0045)</span><br/>
      <span class="footnote">[where .0045 is 144/32000 to convert oz-gross to tons]</span>
    </div>    
    <div class="detail">
      <b>Furnace Utilization</b> is calculated from data in the Materials Usage / Commodity
       File in the legacy AS400 system, summarized by plant, tank, and date.
      That data is compared against the Nominal Capacity for each furnace.  
      The Nominal Capacity values are pulled from yearly spreadsheets maintained at corporate.  The
       Nominal and Environmental Capacities are listed below the titles in the Month to Date and
        Year to Date Furnace Utilization charts for reference.<br/>
    </div>
    <div class="detail">
      <span class="formula">Furnace Utilization = Tons Melted / Nominal Capacity * # of days * 100</span> 
    </div>    
    <div class="detail">
      The Furnace Utilization line markers are <span class="red">red</span> for any percent <b>below
       95</b> and <span class="green">green</span> for anything <b>95% and above</b>.     
    </div>
    `;

  // Monthly Furnace Chart Config
  monthlyFurnaceChartConfig: any = {
    chart: {
      alignTicks: true,
      zoomType: 'xy',
      height: chartHeight,
      spacingBottom: 5,
      borderWidth: 2,
      borderColor: '#DDDDDD'
    },
    credits: {
      enabled: false
    },
    title: {
      text: '',
      margin: 0,
      useHTML: true
    },
    subtitle: {
      text: legendInstructions,
      align: 'center',
      verticalAlign: 'bottom',
      y: -5,
      style: {
        color: gray
      }
    },
    plotOptions: {
      column: {
        dataLabels: {
          enabled: true,
          rotation: -90,
          color: white,
          align: 'right',
          format: '{point.y:.1f}', // one decimal
          y: 10, // 10 pixels down from the top
          style: {
            fontSize: '11px',
            fontFamily: 'Verdana, sans-serif'
          }
        }
      },
      spline: {
        marker: {
          radius: 7
        }
      }
    },
    xAxis: {
      categories: []
    },
    tooltip: {
      valueDecimals: 1
    },
    yAxis: [{
      title: {
        text: tonsText
      }
    },
    {
      title: {
        text: perecentText
      },
      endOnTick: false,
      startOnTick: false,
      opposite: true,
      tickInterval: 10,
      min: 40,
      max: 100
    }],
    series: [{
      type: 'column',
      data: [],
      color: black,
      name: ''
    },
    {
      type: 'column',
      data: [],
      name: '',
      color: darkBlue
    },
    {
      type: 'spline',
      name: furnaceUtilizationText,
      data: [],
      yAxis: 1,
      color: red,
      tooltip: {
        headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>'
          + '<span style="color:{point.color}">●</span><span>' + furnaceUtilizationText + ':</span>',
        pointFormat: '<b>{point.y}%</b>'
      },
      zones: [
        {
          value: 95,
          color: red // red for anything below 95
        },
        {
          color: green // green for anything 95% and above
        }]
    }],
    responsive: {
      rules: [{
        condition: {
          maxWidth: 400
        },
        chartOptions: {
          plotOptions: {
            column: {
              dataLabels: {
                enabled: false
              }
            }
          }
        }

      }]
    }
  };

  monthlyFurnaceHelpText: string = `
  <div class="header">
    Year to Date Furnace Utilization
  </div>
  <div class="subheader">
    <span> Data Source: </span>
    <span class="detail">Legacy AS400</span>
  </div>    
  <div class="detail">
    The chart displays the <b>Tons Melted, Tons Packed, and Furnace Utilization Percentage</b> for
     the selected tank, for each month in the current year.
  </div>    
  <div class="detail">
    <b>Tons Melted</b> is taken from the Materials Usage / Commodity File in the legacy AS400 system,
     summarized by plant, tank, and date.<br/><br/>
    <span class="formula">Tons Melted = SUM(LBS * Yield / 100 / 2000)</span><br/>
    <span class="footnote">[where LBS is # of batches * pounds of material and Yield is % of material consumed from Commodity File]</span>
  </div>
  <div class="detail">
    <b>Tons Packed</b> is taken from Job Records in the legacy AS400 system, summarized by plant, tank, and date.<br/><br/>
    <span class="formula">Tons Packed = SUM(Weight * Gross * .0045)</span><br/>
    <span class="footnote">[where .0045 is 144/32000 to convert oz-gross to tons]</span>
  </div>    
  <div class="detail">
    <b>Furnace Utilization</b> is calculated from data in the Materials Usage / Commodity
     File in the legacy AS400 system, summarized by plant, tank, and date.
    That data is compared against the Nominal Capacity for each furnace.  
    The Nominal Capacity values are pulled from yearly spreadsheets maintained at corporate.  The
     Nominal and Environmental Capacities are listed below the titles in the Month to Date and Year
      to Date Furnace Utilization charts for reference.<br/>
  </div>
  <div class="detail">
    <span class="formula">Furnace Utilization = Tons Melted / Nominal Capacity * # of days * 100</span> 
  </div>    
  <div class="detail">
    The Furnace Utilization line markers are <span class="red">red</span> for any percent <b>below
     95</b> and <span class="green">green</span> for anything <b>95% and above</b>.     
  </div>
    `;

  // YTD Furnace Chart Config
  ytdFurnaceChartConfig: any = {
    chart: {
      type: 'solidgauge',
      width: 280,
      height: 100
    },
    title: "null",
    pane: {
      center: ['30%', '85%'],
      size: '170%',
      startAngle: -90,
      endAngle: 90,
      background: {
        backgroundColor:
          Highcharts.defaultOptions.legend.backgroundColor || '#EEE',
        innerRadius: '75%',
        outerRadius: '100%',
        shape: 'arc'
      }
    },
    tooltip: {
      enabled: false
    },
    credits: {
      enabled: false
    },
    yAxis: {
      min: 0,
      max: 100,
      stops: [],
      lineWidth: 0,
      minorTickInterval: null,
      tickAmount: 2,
      title: {
        text: '',
        useHTML: true,
        y: -30,
        x: 50
      },
      labels: {
        y: 16,
      }
    },
    plotOptions: {
      solidgauge: {
        innerRadius: '75%',
        dataLabels: {
          y: 5,
          borderWidth: 0,
          useHTML: true
        }
      }
    },
    series: [{
      data: [],
      dataLabels: {
        format: '',
        x: 0,
        y: 6
      }
    }]
  };

  ytdFurnaceHelpText: string = `
    <div class="header">
      YTD Furnace Utilization
    </div>
    <div class="subheader">
      <span> Data Source: </span>
      <span class="detail">Legacy AS400</span>
    </div>    
    <div class="detail">
      This gauge chart displays the year-to-date <b>Furnace Utilization</b> percentage for the selected tank.
    </div>    
    <div class="detail">
      The <b>Furnace Utilization %</b> determines the color of the gauge chart.  It will show
       as <span class="red">red</span> for any furnace utilization <b>below 95</b> and
        <span class="green">green</span> for anything <b>95% and above</b>.      
    </div>
    <div class="detail">
      Furnace Utilization is calculated from data in the Materials Usage / Commodity File in the
       legacy AS400 system, summarized by plant, tank, and date.
      That data is compared against the Nominal Capacity for each furnace.  
      The Nominal Capacity values are pulled from yearly spreadsheets maintained at corporate.  The
       Nominal and Environmental Capacities are listed below the titles in the Month to Date and
        Year to Date Furnace Utilization charts for reference.<br/>
    </div>
    <div class="detail">
      <span class="formula">Furnace Utilization = Melted Tons / Nominal Capacity * # of days * 100</span> 
    </div>
    <div class="detail">      
      <span class="formula">Melted Tons = SUM(LBS * Yield / 100 / 2000)</span><br/>
      <span class="footnote">[where LBS is # of batches * pounds of material and Yield is % of material consumed from Commodity File]</span>
    </div>
    `;

  // Daily Line Holds Chart Config
  dailyLineHoldsChartConfig: any = {
    chart: {
      alignTicks: true,
      zoomType: 'xy',
      height: chartHeight,
      spacingBottom: 5,
      borderWidth: 2,
      borderColor: '#DDDDDD'
    },
    credits: {
      enabled: false
    },
    title: {
      text: '',
      margin: 0,
      useHTML: true
    },
    subtitle: {
      text: legendInstructions,
      align: 'center',
      verticalAlign: 'bottom',
      y: -5,
      style: {
        color: gray
      }
    },
    plotOptions: {
      column: {
        dataLabels: {
          enabled: true,
          rotation: -90,
          color: white,
          align: 'right',
          format: '{point.y:.1f}', // one decimal
          y: 10, // 10 pixels down from the top
          style: {
            fontSize: '11px',
            fontFamily: 'Verdana, sans-serif'
          }
        }
      },
      spline: {
        marker: {
          radius: 7
        }
      }
    },
    xAxis: {
      categories: []
    },
    tooltip: {
      valueDecimals: 1
    },
    yAxis: [
      {
        title: {
          text: perecentText
        },
        endOnTick: false,
        startOnTick: false,
        tickAmount: 4
      }],
    series: [
      {
        type: 'spline',
        name: percentLineHoldsText,
        data: [],
        color: darkBlue,
        tooltip: {
          headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>'
            + '<span style="color:{point.color}">●</span><span>' + percentLineHoldsText + ':</span>',
          pointFormat: '<b>{point.y}%</b>'
        },
      }],
    responsive: {
      rules: [{
        condition: {
          maxWidth: 400
        },
        chartOptions: {
          plotOptions: {
            column: {
              dataLabels: {
                enabled: false
              }
            }
          }
        }

      }]
    }
  };

  // Daily Line Holds help text
  lineHoldsHelpText: string = `
  <div class="header">
    Month to Date % Line Holds
  </div>
  <div class="subheader">
    <span> Data Source: </span>
    <span class="detail">Legacy AS400</span>
  </div>    
  <div class="detail">
    The chart displays <b>% Line Holds</b> for each day in the selected month.       
  </div>  
  <div class="detail">
    % Line Holds is calculated from data in Job Records in the legacy AS400 system.  
    All records where speed > 0 and production hours > 0 are included.<br/>
  </div>
  <div class="detail">
    <span class="formula">% Line Holds = rounded to tenths(Line Holds / Gross * 100)</span><br/>
    <span class="footnote">[where gross > 0]</span><br/><br/>
  </div>
  `;
}

export default angular
  .module('aps.services.chartConfigService', [HttpServiceName])
  .service('chartConfigService', ChartConfigServiceImpl)
  .name;


// WEBPACK FOOTER //
// ./src/app/services/dashboard/chartConfig.service.ts